import { FieldType } from "../../type-system/FieldType";
import {
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanKey,
  ShopifySellingPlanToSellingPlanGroupKey,
  ShopifySellingPlanToShopKey,
  ShopifySellingPlanToSubscriptionLinesKey,
  ShopifyShopKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToSellingPlanKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const billingPolicyKey = "ModelField-DataModel-Shopify-SellingPlan-billingPolicy";
const billingPolicyStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-SellingPlan-billingPolicy-initial";
const deliveryPolicyKey = "ModelField-DataModel-Shopify-SellingPlan-deliveryPolicy";
const deliveryPolicyStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-SellingPlan-deliveryPolicy-initial";
const pricingPoliciesKey = "ModelField-DataModel-Shopify-SellingPlan-pricingPolicies";
const pricingPoliciesStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-SellingPlan-pricingPolicies-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySellingPlanKey,
  "2022-01": {
    name: "Shopify Selling Plan",
    fields: [
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        ...on SellingPlanRecurringBillingPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          interval
          intervalCount
          maxCycles
          minCycles
        }`,
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `
        ...on SellingPlanRecurringDeliveryPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          cutoff
          intent
          interval
          intervalCount
          preAnchorBehavior
        }
        `,
          },
        },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "options" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "position" } },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Pricing Policies",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "pricingPolicies",
            graphQLSelection: `
        ...on SellingPlanFixedPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          createdAt
        }
        ...on SellingPlanRecurringPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          afterCycle
          createdAt
        }`,
          },
        },
      },
      {
        name: "Selling Plan Group",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanToSellingPlanGroupKey,
        configuration: {
          relatedModelKey: ShopifySellingPlanGroupKey,
        },
        // this field is not in the Shopify GraphQL API but we do sync it with our payloader processor
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sellingPlanGroupId", skipGraphQLSelection: true } },
      },
      {
        name: "Subscription Lines",
        fieldType: FieldType.HasMany,
        key: ShopifySellingPlanToSubscriptionLinesKey,
        configuration: {
          relatedModelKey: ShopifySubscriptionLineKey,
          inverseFieldKey: ShopifySubscriptionLineToSellingPlanKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "sellingPlanGroup.sellingPlans",
      parent: "sellingPlanGroups",
    },
  },
  "2022-07": {
    fields: [
      {
        name: "Category",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "category", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          options: [{ name: "OTHER" }, { name: "PRE_ORDER" }, { name: "SUBSCRIPTION" }, { name: "TRY_BEFORE_YOU_BUY" }],
        },
      },
      {
        name: "Inventory Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "inventoryPolicy", graphQLSelection: "reserve" } },
      },
    ],
  },
  "2024-07": {
    hasMetafields: true,
  },
  "2025-04": {
    fields: [
      {
        name: "Billing Policy",
        key: billingPolicyKey,
        initialStorageEpochKey: billingPolicyStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            shopifyFieldKey: "billing_policy",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            graphQLSelection: `
        ...on SellingPlanRecurringBillingPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          interval
          intervalCount
          maxCycles
          minCycles
        }`,
          },
        },
      },
      {
        name: "Delivery Policy",
        key: deliveryPolicyKey,
        initialStorageEpochKey: deliveryPolicyStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            shopifyFieldKey: "delivery_policy",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            graphQLSelection: `
        ...on SellingPlanRecurringDeliveryPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          cutoff
          intent
          interval
          intervalCount
          preAnchorBehavior
        }
        `,
          },
        },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "description",
            shopifyFieldKey: "description",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "name",
            shopifyFieldKey: "name",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
          },
        },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "options",
            shopifyFieldKey: "options",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
          },
        },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "position",
            shopifyFieldKey: "position",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
          },
        },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Pricing Policies",
        key: pricingPoliciesKey,
        initialStorageEpochKey: pricingPoliciesStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "pricingPolicies",
            shopifyFieldKey: "pricing_policies",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            graphQLSelection: `
        ...on SellingPlanFixedPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          createdAt
        }
        ...on SellingPlanRecurringPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          afterCycle
          createdAt
        }`,
          },
        },
      },
    ],
  },
};

export const ShopifySellingPlan = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
